import { useCallback, useMemo, useState } from "react";

import { formatDuration, formatPercentage, round } from "~/utils/helpers";
import { useLabels } from "~/hooks/useLabels";
import { Icon } from "~/components/icon/Icon";
import { Truncate } from "~/components/truncate/Truncate";
import { Link } from "~/components/link/Link";
import { Text } from "~/components/text/Text";
import { ContentActionsModal } from "~/components/contentActionsModal/ContentActionsModal";
import { ProgressBar } from "~/components/progressBar/ProgressBar";
import { MetaDataList } from "~/components/metaDataList/MetaDataList";

import { RawHtml } from "../rawHtml/RawHtml";

import "./SessionCard.css";

import type { SyntheticEvent } from "react";
import type { ActionProp } from "~/components/components";

export interface SessionCardStateAndHelpers {
  closeActions: () => void;
}

export interface SessionCardProps {
  renderActions?: (stateAndHelpers: SessionCardStateAndHelpers) => ActionProp[];
  artwork: string;
  description?: Maybe<string>;
  duration?: number;
  href: string;
  meta?: string[];
  noteCount?: number;
  position?: number;
  progress?: number;
  title: string;
  onClick?: () => void;
}

const DEFAULT_LABELS = {
  "common.cta.actions": "Actions",
  "card.progress": "{{percent}} Complete",
  "card.session.meta.notes_one": "{{count}} Note",
  "card.session.meta.notes_other": "{{count}} Notes",
};

export function SessionCard({
  renderActions,
  artwork,
  description,
  duration,
  href,
  meta,
  noteCount = 0,
  progress = 0,
  title,
  onClick,
}: SessionCardProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const [isActionsOpen, setIsActionsOpen] = useState(false);

  const preTitle = useMemo(() => {
    return (
      <MetaDataList>
        {meta?.map((m) => <MetaDataList.Item key={m}>{m}</MetaDataList.Item>)}
      </MetaDataList>
    );
  }, [meta]);

  const progressLabel = useMemo(() => {
    if (!duration) return;

    // Round the number to the hundreth to avoid displaying progress < 1%
    const roundedProgress = round(progress);

    if (!roundedProgress || (roundedProgress && roundedProgress >= 1)) {
      return formatDuration(duration, t);
    }

    return t("card.progress", { percent: formatPercentage(roundedProgress) });
  }, [duration, progress, t]);

  const handleActionsClick = useCallback((e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsActionsOpen(true);
  }, []);

  const handleCloseActions = useCallback(() => {
    setIsActionsOpen(false);
  }, []);

  const actions = useMemo(() => {
    if (!renderActions) return [];
    return renderActions({ closeActions: handleCloseActions });
  }, [handleCloseActions, renderActions]);

  const hasActions = Boolean(actions?.length);

  return (
    <>
      <Link
        className="SessionCard"
        to={href}
        nativeBehavior="presentFullScreen"
        nativeStencil={{
          propsJSON: "{}",
          type: "session",
        }}
        onClick={onClick}
      >
        <div className="SessionCard-artwork">
          <img
            src={artwork}
            decoding="async"
            loading="lazy"
            alt={title}
            title={title}
          />
        </div>

        <div className="SessionCard-body">
          {preTitle}

          <Text preset="title" weight="semibold">
            <Truncate lines={2}>{title}</Truncate>
          </Text>

          <Text preset="description">
            <Truncate lines={3}>
              <RawHtml html={description ?? undefined} />
            </Truncate>
          </Text>
        </div>

        <div className="SessionCard-footer">
          <div className="SessionCard-footer-section">
            {progress > 0 && progress < 1 && (
              <ProgressBar size="sm" value={progress} />
            )}

            <Text size="2xs" weight="medium" lineHeight="tight">
              {progressLabel}
            </Text>
          </div>

          <div className="SessionCard-footer-section">
            {noteCount > 0 && (
              <Text size="2xs" color="gray" lineHeight="tight">
                {t("card.session.meta.notes", { count: noteCount })}
              </Text>
            )}

            {progress === 1 && (
              <Icon
                icon="circle-check"
                variant="solid"
                size="sm"
                color="var(--color-brand-accessible)"
              />
            )}
          </div>
        </div>

        {hasActions && (
          <button
            aria-label={t("common.cta.actions")}
            className="SessionCard-actions"
            onClick={handleActionsClick}
          >
            <Icon icon="ellipsis" />
          </button>
        )}
      </Link>

      {hasActions && (
        <ContentActionsModal
          actions={actions}
          artwork={artwork}
          isOpen={isActionsOpen}
          postTitle={preTitle}
          title={title}
          onRequestClose={handleCloseActions}
        />
      )}
    </>
  );
}
